import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AiOutlinePlus } from 'react-icons/ai';
import { MdOutlineCancel } from "react-icons/md";
import { httpsCallable } from 'firebase/functions';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { ShowErrorToast } from '../../Common/Toast/toast';
import { curriculumSx, ErrorBlockSmall, SectionQuestion } from '../styles';
import { functions } from '../../../Infrastructure/config';
import {
  StyledOutlinedTextField
} from '../../../Infrastructure/Theme/styledComponents';
import { StepsHeader } from '../../Common/StepsHeader/stepsHeader';
import { StepsFooter } from '../../Common/StepsFooter/stepsFooter';
import { decrementCoursesSteps, incrementCoursesSteps } from '../../../Infrastructure/States/coursesStepsSlice';
import { Loader } from '../../Common/Loader/loader';
import { cleanCourseState } from '../../../Infrastructure/States/courseDetailsSlice';
import { setCourseDetails } from '../../../Infrastructure/States/courseDetailsSlice';
import { USER_LOGS_CLIENT } from '../../../Infrastructure/Constants/logsConstant';
import useAuthentication from '../../../Infrastructure/States/onAuthStateChange';
import Grid from "@mui/material/Unstable_Grid2";
import ERROR_CONSTANTS from '../../../Infrastructure/Constants/errorConstants';
import paths from '../../../Infrastructure/Router/paths';
import { safeClientLogEvent } from '../../../Infrastructure/Utilities/safeClientLogEvent';

export const Curriculum = () => {
    const theme = useTheme();
    const errorRef = useRef(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { pathname } = useLocation()
    const {userId} = useAuthentication()
    const [showError, setShowError] = useState(false)
    const [isLoading, setIsLoading] = useState(false);
    const ip = useSelector((state) => state.ip.address)
    const [scrollTrigger, setScrollTrigger] = useState(0);
    const isXs = useMediaQuery(theme.breakpoints.down('sm'));
    const page = useSelector((state)=> state.route.currentRoute)
    const userData = useSelector((state) => state.userData.data)
    const course = useSelector((state) => state.course.courseDetails)
    const sectionsData = course?.course?.courseDetails?.curriculum?.sections
    const courseId = course?.courseId || null
    const defaultSections = sectionsData?.map(section => ({
        title: section?.title || '',
        description: section?.description || '',
    }));
    const [sections, setSections] = useState(defaultSections || [{
        title: '',
        description: '',
    }]);
    const validSections = sections?.filter(section => section?.title.trim() && section?.description.trim());
    const courseSteps = useSelector((state) => state.courseSteps.courseSteps);

    const handleExit = async () => {
        try {
            setIsLoading(true)
            const updateCurriculumStep = httpsCallable(functions, "updatecurriculum");
            await updateCurriculumStep({ courseId, sections, step:3, ip, page });
            navigate(paths.dashboardPage.path);
        } catch (error) {
            navigate(paths.dashboardPage.path);
            ShowErrorToast(ERROR_CONSTANTS.GENERIC_ERROR_TOAST)
            await safeClientLogEvent({
              ...USER_LOGS_CLIENT.CURRICULUM_UPDATE_FAILED,
              uid: userId || null,
              source: "curriculum.jsx",
              emailInfo: null,
              metadata: { courseId, step: 3 },
              error: error?.message || null
            });
        } finally {
            dispatch(cleanCourseState())
            setIsLoading(false)
        }
    };
    const handleDec = async () => {
        if (courseSteps > 1) {
            try {
                setIsLoading(true)
                const updateCurriculumStep = httpsCallable(functions, "updatecurriculum");
                await updateCurriculumStep({ courseId, sections, step:2, ip, page });
                dispatch(decrementCoursesSteps());
                const course = userData?.educator?.courses[courseId ? courseId : ''];
                if (course) {
                    dispatch(setCourseDetails({ course, courseId }));
                }
            } catch (error) {
                ShowErrorToast(error);
                await safeClientLogEvent({
                  ...USER_LOGS_CLIENT.CURRICULUM_STEP_DECREMENT_FAILED,
                  uid: userId || null,
                  source: "curriculum.jsx",
                  emailInfo: null,
                  metadata: { courseId, step: 2 },
                  error: error?.message || null
                });
            } finally {
                setIsLoading(false)
            }
        }
    };
    const handleAddSection = () => {
        if (sections.length < 20) {
            setSections(prevSections => [...prevSections, { title: '', description: '' }]);
        } else {
            setShowError("Maximum 20 sections are allowed");
        }
    };
    const handleTitleChange = (index, event) => {
        const newSections = [...sections];
        newSections[index] = { ...newSections[index], title: event.target.value };
        setSections(newSections);
    };
    const handleDescriptionChange = (index, event) => {
        const newSections = [...sections];
        newSections[index] = { ...newSections[index], description: event.target.value };
        setSections(newSections);
    };
    const handleContinueClick = async () => {
        if (courseSteps > 1) {
            const hasIncomplete = sections.some(
            (section) =>
                (section.title.trim() && !section.description.trim()) ||
                (!section.title.trim() && section.description.trim())
            );
            if (hasIncomplete) {
                setShowError("Please fill both title and description for each section.");
                setScrollTrigger(prev => prev + 1)
                return;
            }
            if (validSections.length < 3) {
                setShowError("At least 3 completed sections are required.");
                setScrollTrigger(prev => prev + 1)
                return;
            }
            if (validSections.length > 20) {
                setShowError("Maximum 20 sections are allowed.");
                setScrollTrigger(prev => prev + 1)
                return;
            }
            try {
                setShowError(false);
                setIsLoading(true)
                const updateCurriculumStep = httpsCallable(functions, "updatecurriculum");
                await updateCurriculumStep({ courseId, sections: validSections, ip, page });
                const course = userData?.educator?.courses[courseId ? courseId : ''];
                if (course) {
                    dispatch(setCourseDetails({ course, courseId }));
                }
                dispatch(incrementCoursesSteps());
            } catch (error) {
                setShowError("Something went wrong? Try again.");
                await safeClientLogEvent({
                  ...USER_LOGS_CLIENT.CURRICULUM_UPDATE_FAILED,
                  uid: userId || null,
                  source: "curriculum.jsx",
                  emailInfo: null,
                  metadata: { courseId, sectionsCount: validSections.length },
                  error: error?.message || null
                });
            } finally {
                setIsLoading(false)
            }
        }
    };
    const handleRemoveSection = (index) => {
        const updatedSections = [...sections];
        updatedSections.splice(index, 1);
        setSections(updatedSections);
    };
    useEffect(() => {
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
    }, [pathname])
    useEffect(() => {
        if (showError && isXs && errorRef.current) {
            errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [scrollTrigger, isXs, showError]);

    return (
        <Box 
            sx={curriculumSx.root}>
            {/* Header Box */}
            <Box sx={curriculumSx.headerBox}>
                <StepsHeader steps={courseSteps} handleExit={handleExit} />
            </Box>
            {
                isLoading ? <Loader/> :
                    <Box 
                       sx={curriculumSx.content}
                    >
                        <Box sx={curriculumSx.sectionsBox}>
                            <Grid container spacing={2}>
                                {sections?.map((section, index) => (
                                    <Grid xs={12} md={6} key={index}>
                                        <Box sx={curriculumSx.sectionBox}>
                                            <Box sx={curriculumSx.sectionHeaderBox}>
                                                <SectionQuestion variant="h6">New Section</SectionQuestion>
                                                {sections?.length > 3 && (
                                                    <MdOutlineCancel
                                                        style={{ fontSize: "2em", cursor: "pointer" }}
                                                        onClick={() => handleRemoveSection(index)}
                                                    />
                                                )}
                                            </Box>
                                            <StyledOutlinedTextField
                                                name="title"
                                                label="Title"
                                                variant="outlined"
                                                onChange={(event) => handleTitleChange(index, event)}
                                                value={section?.title}
                                                fullWidth
                                                sx={curriculumSx.textField}
                                            />
                                            <SectionQuestion variant="h6" sx={{ mt: 2 }}>
                                                Overview of what content will be covered in this section
                                            </SectionQuestion>
                                            <StyledOutlinedTextField
                                                name="description"
                                                label="Description"
                                                variant="outlined"
                                                onChange={(event) => handleDescriptionChange(index, event)}
                                                value={section?.description}
                                                multiline
                                                rows={2}
                                                fullWidth
                                                sx={curriculumSx.textField}
                                            />
                                        </Box>
                                    </Grid>
                                ))}

                                {/* Add Section Box */}
                                <Grid xs={12} md={6}>
                                    <Box
                                        border={1}
                                        p={2}
                                        sx={curriculumSx.addSectionBox}
                                        onClick={handleAddSection}
                                    >
                                        <Button
                                            variant="text"
                                            color="secondary"
                                            startIcon={<AiOutlinePlus />}
                                            sx={curriculumSx.addSectionBoxButton}
                                        >
                                            Add Section
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                        {
                        showError && <ErrorBlockSmall mt={5} ref={errorRef}>
                                {showError}
                            </ErrorBlockSmall>
                        }
                    </Box>
            }
            <Box sx={curriculumSx.footerBox}>
                <StepsFooter handleDec={handleDec} handleContinueClick={handleContinueClick} error={showError}  isLoading={isLoading}/>
            </Box>
        </Box>
    );
};
